<template>
  <div class="page">
    <div class="head">
      <div><h1>玩家与小屋</h1><p>看看谁在农场里，以及大家的小屋安排。</p></div>
      <el-button :loading="loading" @click="refresh">刷新</el-button>
    </div>
    <el-alert type="info" :closable="false" title="玩家信息来自正在运行的游戏"
      description="先启动游戏服务器，好友加入后就能看到在线玩家。重要的玩家权限变更需要你亲自确认。" />
    <div class="overview">
      <div class="stat"><small>游戏状态</small><strong>{{ready?'已就绪':'已停止'}}</strong></div>
      <div class="stat"><small>在线玩家</small><strong>{{ready?onlineCount:'—'}}</strong></div>
    </div>
    <div class="panel">
      <h3>在线玩家</h3>
      <el-table :data="playerList" :empty-text="ready?'目前没有在线玩家':'服务器未启动，暂时看不到玩家'" style="width:100%;margin-top:15px">
        <el-table-column label="玩家" min-width="150">
          <template #default="{row}">{{row.name||row.Name||row.displayName||'未命名玩家'}}</template>
        </el-table-column>
        <el-table-column label="当前状态" min-width="130">
          <template #default="{row}">{{row.status||row.Status||'正在游玩'}}</template>
        </el-table-column>
        <el-table-column label="管理" min-width="125">
          <template #default="{row}"><el-button size="small" type="primary" plain @click="grant(row)">设为管理员</el-button></template>
        </el-table-column>
      </el-table>
      <p class="hint">成为管理员后，好友也能管理游戏中的部分事务，请只授权你信任的人。</p>
    </div>
    <div class="panel">
      <h3>农场助手与小屋</h3>
      <div v-if="!ready" class="friendly-empty">
        <span class="empty-icon" aria-hidden="true">🏠</span><strong>服务器还没有启动</strong>
        <p>启动后就能查看农场中的小屋和角色安排。</p>
        <router-link to="/game">前往启动游戏服务器 →</router-link>
      </div>
      <el-table v-else-if="cabinList.length" :data="cabinList" style="width:100%;margin-top:16px">
        <el-table-column label="居住玩家" min-width="160">
          <template #default="{row}">{{row.ownerName||row.playerName||row.owner||'暂无主人'}}</template>
        </el-table-column>
        <el-table-column label="使用情况" min-width="130">
          <template #default="{row}">{{row.occupied||row.hasOwner?'已有人居住':'可供玩家入住'}}</template>
        </el-table-column>
      </el-table>
      <div v-else class="friendly-empty">
        <span class="empty-icon" aria-hidden="true">🏠</span>
        <strong>暂时没有可查看的小屋资料</strong>
        <p>好友加入农场后，可再回到这里查看。</p>
      </div>
      <p class="hint">如果需要转让玩家角色，请先确认双方的账号身份，避免角色被其他玩家误用。</p>
    </div>
  </div>
</template>
<script setup>
import {ref,computed,onMounted} from 'vue'
import {ElMessage,ElMessageBox} from 'element-plus'
import {http} from '../plugins/axios'
import {authErrorMessage} from '../auth'
const loading=ref(false),ready=ref(false),players=ref(null),cabins=ref(null)
const playerList=computed(()=>Array.isArray(players.value)?players.value:Array.isArray(players.value?.players)?players.value.players:[])
const onlineCount=computed(()=>playerList.value.length)
const cabinList=computed(()=>{
 const raw=cabins.value
 const list=Array.isArray(raw)?raw:Array.isArray(raw?.cabins)?raw.cabins:Array.isArray(raw?.slots)?raw.slots:[]
 return list.filter(x=>x && typeof x==='object')
})
async function refresh(){
 loading.value=true
 try {
  const [status,response]=await Promise.all([http.get('/api/manage/game/status'),http.get('/api/manage/admin/players')])
  ready.value=!!status.data.ready;players.value=response.data.players;cabins.value=response.data.cabins
 } catch(e) {ElMessage.error(authErrorMessage(e,'读取玩家信息失败'))}
 finally {loading.value=false}
}
async function grant(row){
 const name=row.name||row.Name||row.displayName
 if(!name){ElMessage.warning('当前玩家没有可用的名称');return}
 try {await ElMessageBox.confirm('确定授予「'+name+'」游戏管理员权限吗？请只授权信任的玩家。','确认玩家权限',{type:'warning',confirmButtonText:'确认授权'})}
 catch(_){return}
 try {
  await http.post('/api/manage/admin/players/grant-admin',{name})
  ElMessage.success('已提交授权，请查看玩家状态')
  await refresh()
 } catch(e){ElMessage.error(authErrorMessage(e,'授权失败'))}
}
onMounted(refresh)
</script>
<style scoped>
.page{max-width:1100px}.head{display:flex;align-items:center;justify-content:space-between;gap:15px;margin-bottom:22px}h1{margin:0;font-size:27px}h3{font-size:17px;margin:0}.head p,.hint{font-size:13px;color:#718178;line-height:1.8}.overview{display:flex;gap:14px;margin:18px 0}.stat{background:#fff;border:1px solid #e5ece5;border-radius:12px;padding:19px;min-width:165px}.stat small{display:block;color:#77887b}.stat strong{display:block;font-size:21px;margin-top:9px}.panel{background:#fff;border:1px solid #e6eee7;border-radius:14px;padding:24px;margin-bottom:20px}.friendly-empty{text-align:center;background:#f6f8ef;border-radius:12px;padding:28px 14px;margin:15px 0}.empty-icon{font-size:34px;display:block;margin-bottom:8px}.friendly-empty strong{font-size:14px;color:#456b4d}.friendly-empty p{font-size:12px;color:#738778}.friendly-empty a{display:inline-block;font-size:13px;color:#477f52;font-weight:700;margin-top:4px}@media(max-width:650px){.head{flex-wrap:wrap}.panel{padding:16px}.stat{flex:1;min-width:0}}
</style>
